import {
  parseBookedTreatmentCourse,
} from '@/lib/bookedTreatmentCourse';
import type { QuestionnaireAutomationDraft } from '@/lib/preVisitQuestionnaireTiming';
import type {
  BookedTreatmentCourse,
  BookedTreatmentCourseStep,
} from '@/types/treatmentCourses';

export type StaffCourseNamedRef = {
  id: string;
  name: string;
};

export type StaffCourseQuestionnaire = {
  automation_id: string | null;
  forms_template_id: string;
  template_name: string | null;
  phase: QuestionnaireAutomationDraft['phase'];
  job_id: string | null;
  job_status: string | null;
  job_reason: string | null;
  scheduled_for: string | null;
  attempted_at: string | null;
  assignment_id: string | null;
  forms_assignment_id: string | null;
  assignment_status: string | null;
  sent_at: string | null;
  completed_at: string | null;
  score: Record<string, unknown> | null;
};

export type StaffCourseStep = BookedTreatmentCourseStep & {
  appointment_status: string | null;
  appointment_source: string | null;
  has_journal_note: boolean;
  journal_entry_id: string | null;
  practitioners: StaffCourseNamedRef[];
  resources: StaffCourseNamedRef[];
  questionnaires: StaffCourseQuestionnaire[];
};

export type StaffTreatmentCourse = Omit<BookedTreatmentCourse, 'steps'> & {
  created_at: string | null;
  started_at: string | null;
  booked_by: string | null;
  steps: StaffCourseStep[];
};

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asNamedRefs(value: unknown): StaffCourseNamedRef[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item): StaffCourseNamedRef | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const id = asString(row.id);
      if (!id) return null;
      return { id, name: asString(row.name) ?? id };
    })
    .filter((item): item is StaffCourseNamedRef => item !== null);
}

function asQuestionnaire(value: unknown): StaffCourseQuestionnaire | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const formsTemplateId = asString(row.forms_template_id);
  if (!formsTemplateId) return null;
  return {
    automation_id: asString(row.automation_id),
    forms_template_id: formsTemplateId,
    template_name: asString(row.template_name),
    phase: row.phase === 'after_appointment' ? 'after_appointment' : 'before_appointment',
    job_id: asString(row.job_id),
    job_status: asString(row.job_status),
    job_reason: asString(row.job_reason),
    scheduled_for: asString(row.scheduled_for),
    attempted_at: asString(row.attempted_at),
    assignment_id: asString(row.assignment_id),
    forms_assignment_id: asString(row.forms_assignment_id),
    assignment_status: asString(row.assignment_status),
    sent_at: asString(row.sent_at),
    completed_at: asString(row.completed_at),
    score:
      row.score && typeof row.score === 'object' && !Array.isArray(row.score)
        ? (row.score as Record<string, unknown>)
        : null,
  };
}

export function parseStaffTreatmentCourse(raw: unknown): StaffTreatmentCourse | null {
  const course = parseBookedTreatmentCourse(raw);
  if (!course) return null;
  const row = raw as Record<string, unknown>;
  const stepsRaw = Array.isArray(row.steps) ? row.steps : [];
  const byId = new Map<string, Record<string, unknown>>();
  for (const item of stepsRaw) {
    if (!item || typeof item !== 'object') continue;
    const step = item as Record<string, unknown>;
    if (typeof step.id === 'string') byId.set(step.id, step);
  }

  const steps: StaffCourseStep[] = course.steps.map((step) => {
    const extra = byId.get(step.id) ?? {};
    const questionnairesRaw = Array.isArray(extra.questionnaires) ? extra.questionnaires : [];
    return {
      ...step,
      appointment_status: asString(extra.appointment_status),
      appointment_source: asString(extra.appointment_source),
      has_journal_note: extra.has_journal_note === true,
      journal_entry_id: asString(extra.journal_entry_id),
      practitioners: asNamedRefs(extra.practitioners),
      resources: asNamedRefs(extra.resources),
      questionnaires: questionnairesRaw
        .map(asQuestionnaire)
        .filter((item): item is StaffCourseQuestionnaire => item !== null),
    };
  });

  const firstStart = steps
    .map((step) => step.starts_at)
    .filter((value): value is string => Boolean(value))
    .sort()[0];

  return {
    ...course,
    created_at: asString(row.created_at),
    started_at: asString(row.started_at) ?? firstStart ?? asString(row.created_at),
    booked_by: asString(row.booked_by),
    steps,
  };
}

export type StaffCourseEnrichmentSources = {
  appointments?: Array<{
    id: string;
    status?: string | null;
    source?: string | null;
  }>;
  jobs?: Array<{
    id?: string;
    appointment_id?: string | null;
    treatment_course_step_automation_id?: string | null;
    forms_template_id?: string | null;
    status?: string | null;
    reason?: string | null;
    scheduled_for?: string | null;
    attempted_at?: string | null;
    created_at?: string | null;
  }>;
  assignments?: Array<{
    id: string;
    appointment_id?: string | null;
    forms_assignment_id?: string | null;
    forms_template_id: string;
    template_summary?: { name?: string } | null;
    status?: string | null;
    sent_at?: string | null;
    completed_at?: string | null;
    score?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
  }>;
  journals?: Array<{ id: string; appointment_id?: string | null }>;
  memberNames?: ReadonlyMap<string, string>;
  resourceNames?: ReadonlyMap<string, string>;
  templateNames?: ReadonlyMap<string, string>;
};

function jobRecency(job: { created_at?: string | null; scheduled_for?: string | null; id?: string }): number {
  const created = job.created_at ? Date.parse(job.created_at) : Number.NaN;
  if (!Number.isNaN(created)) return created;
  const scheduled = job.scheduled_for ? Date.parse(job.scheduled_for) : Number.NaN;
  if (!Number.isNaN(scheduled)) return scheduled;
  return 0;
}

export function pickLatestMatchingJob<
  T extends {
    id?: string;
    appointment_id?: string | null;
    treatment_course_step_automation_id?: string | null;
    forms_template_id?: string | null;
    created_at?: string | null;
    scheduled_for?: string | null;
  },
>(
  jobs: readonly T[] | undefined,
  appointmentId: string | null,
  automationId: string | null,
  formsTemplateId: string,
): T | null {
  if (!jobs || !appointmentId) return null;
  const matches = jobs.filter((job) => {
    if (job.appointment_id !== appointmentId) return false;
    if (automationId && job.treatment_course_step_automation_id === automationId) return true;
    return job.forms_template_id === formsTemplateId;
  });
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => {
    const recency = jobRecency(b) - jobRecency(a);
    if (recency !== 0) return recency;
    return (b.id ?? '').localeCompare(a.id ?? '');
  })[0] ?? null;
}

function matchJob(
  jobs: StaffCourseEnrichmentSources['jobs'],
  appointmentId: string | null,
  automationId: string | null,
  formsTemplateId: string,
) {
  return pickLatestMatchingJob(jobs, appointmentId, automationId, formsTemplateId);
}

function assignmentMatchesStep(
  assignment: NonNullable<StaffCourseEnrichmentSources['assignments']>[number],
  step: Pick<StaffCourseStep, 'id' | 'appointment_id'>,
): boolean {
  if (assignment.appointment_id && step.appointment_id && assignment.appointment_id === step.appointment_id) {
    return true;
  }
  const stepId = assignment.metadata?.treatment_course_step_id;
  return typeof stepId === 'string' && stepId === step.id;
}

export function mergeExtraStepAssignments(
  questionnaires: StaffCourseQuestionnaire[],
  step: Pick<StaffCourseStep, 'id' | 'appointment_id'>,
  assignments: StaffCourseEnrichmentSources['assignments'],
  templateNames?: ReadonlyMap<string, string>,
): StaffCourseQuestionnaire[] {
  if (!assignments?.length) return questionnaires;
  const seen = new Set(
    questionnaires.flatMap((row) =>
      [row.assignment_id, row.forms_assignment_id, row.forms_template_id].filter(Boolean),
    ),
  );
  const extras: StaffCourseQuestionnaire[] = [];
  for (const assignment of assignments) {
    if (!assignmentMatchesStep(assignment, step)) continue;
    if (assignment.status === 'cancelled' || assignment.status === 'expired') {
      continue;
    }
    if (
      seen.has(assignment.id) ||
      (assignment.forms_assignment_id && seen.has(assignment.forms_assignment_id)) ||
      seen.has(assignment.forms_template_id)
    ) {
      continue;
    }
    seen.add(assignment.id);
    extras.push({
      automation_id: null,
      forms_template_id: assignment.forms_template_id,
      template_name: assignment.template_summary?.name ?? templateNames?.get(assignment.forms_template_id) ?? null,
      phase: 'after_appointment',
      job_id: null,
      job_status: null,
      job_reason: null,
      scheduled_for: null,
      attempted_at: null,
      assignment_id: assignment.id,
      forms_assignment_id: assignment.forms_assignment_id ?? null,
      assignment_status: assignment.status ?? null,
      sent_at: assignment.sent_at ?? null,
      completed_at: assignment.completed_at ?? null,
      score: assignment.score ?? null,
    });
  }
  return extras.length === 0 ? questionnaires : [...questionnaires, ...extras];
}

function matchAssignment(
  assignments: StaffCourseEnrichmentSources['assignments'],
  appointmentId: string | null,
  formsTemplateId: string,
) {
  if (!assignments) return null;
  return (
    assignments.find(
      (row) => row.appointment_id === appointmentId && row.forms_template_id === formsTemplateId,
    ) ??
    assignments.find((row) => row.forms_template_id === formsTemplateId && !row.appointment_id) ??
    null
  );
}

export function enrichStaffCourse(
  course: StaffTreatmentCourse,
  sources: StaffCourseEnrichmentSources,
): StaffTreatmentCourse {
  const journalsByAppointment = new Map<string, string>();
  for (const journal of sources.journals ?? []) {
    if (journal.appointment_id && !journalsByAppointment.has(journal.appointment_id)) {
      journalsByAppointment.set(journal.appointment_id, journal.id);
    }
  }
  const appointmentsById = new Map((sources.appointments ?? []).map((row) => [row.id, row]));

  return {
    ...course,
    steps: course.steps.map((step) => {
      const appointment = step.appointment_id
        ? appointmentsById.get(step.appointment_id)
        : undefined;
      const journalId = step.appointment_id
        ? journalsByAppointment.get(step.appointment_id) ?? null
        : null;
      const questionnaires =
        step.questionnaires.length > 0
          ? step.questionnaires
          : step.automations.map((auto) => ({
              automation_id: auto.id,
              forms_template_id: auto.forms_template_id,
              template_name: null,
              phase: auto.phase,
              job_id: null,
              job_status: null,
              job_reason: null,
              scheduled_for: null,
              attempted_at: null,
              assignment_id: null,
              forms_assignment_id: null,
              assignment_status: null,
              sent_at: null,
              completed_at: null,
              score: null,
            }));

      return {
        ...step,
        appointment_status: appointment?.status ?? step.appointment_status,
        appointment_source: appointment?.source ?? step.appointment_source,
        has_journal_note: Boolean(journalId) || step.has_journal_note,
        journal_entry_id: journalId ?? step.journal_entry_id,
        practitioners: step.practitioner_ids.map((id) => ({
          id,
          name: sources.memberNames?.get(id) ?? step.practitioners.find((p) => p.id === id)?.name ?? id,
        })),
        resources: step.resource_ids.map((id) => ({
          id,
          name: sources.resourceNames?.get(id) ?? step.resources.find((r) => r.id === id)?.name ?? id,
        })),
        questionnaires: mergeExtraStepAssignments(
          questionnaires.map((row) => {
          const job = matchJob(
            sources.jobs,
            step.appointment_id,
            row.automation_id,
            row.forms_template_id,
          );
          const assignment = matchAssignment(
            sources.assignments,
            step.appointment_id,
            row.forms_template_id,
          );
          return {
            ...row,
            template_name:
              row.template_name ??
              assignment?.template_summary?.name ??
              sources.templateNames?.get(row.forms_template_id) ??
              null,
            job_id: row.job_id ?? job?.id ?? null,
            job_status: row.job_status ?? job?.status ?? null,
            job_reason: row.job_reason ?? job?.reason ?? null,
            scheduled_for: row.scheduled_for ?? job?.scheduled_for ?? null,
            attempted_at: row.attempted_at ?? job?.attempted_at ?? null,
            assignment_id: row.assignment_id ?? assignment?.id ?? null,
            forms_assignment_id: row.forms_assignment_id ?? assignment?.forms_assignment_id ?? null,
            assignment_status: row.assignment_status ?? assignment?.status ?? null,
            sent_at: row.sent_at ?? assignment?.sent_at ?? null,
            completed_at: row.completed_at ?? assignment?.completed_at ?? null,
            score: row.score ?? assignment?.score ?? null,
          };
        }),
          step,
          sources.assignments,
          sources.templateNames,
        ),
      };
    }),
  };
}

export function staffCourseFromBooked(
  course: BookedTreatmentCourse,
  extras?: Partial<Pick<StaffTreatmentCourse, 'created_at' | 'started_at' | 'booked_by'>>,
): StaffTreatmentCourse {
  return {
    ...course,
    created_at: extras?.created_at ?? null,
    started_at: extras?.started_at ?? course.steps.find((step) => step.starts_at)?.starts_at ?? null,
    booked_by: extras?.booked_by ?? null,
    steps: course.steps.map((step) => ({
      ...step,
      appointment_status: null,
      appointment_source: null,
      has_journal_note: false,
      journal_entry_id: null,
      practitioners: step.practitioner_ids.map((id) => ({ id, name: id })),
      resources: step.resource_ids.map((id) => ({ id, name: id })),
      questionnaires: step.automations.map((auto) => ({
        automation_id: auto.id,
        forms_template_id: auto.forms_template_id,
        template_name: null,
        phase: auto.phase,
        job_id: null,
        job_status: null,
        job_reason: null,
        scheduled_for: null,
        attempted_at: null,
        assignment_id: null,
        forms_assignment_id: null,
        assignment_status: null,
        sent_at: null,
        completed_at: null,
        score: null,
      })),
    })),
  };
}
